<template>
  <aside
    class="flex h-dvh w-fit flex-col bg-[--v3-theme-primarycolor] font-sans shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.10),0px_8px_10px_-6px_rgba(0,0,0,0.10)] transition-[margin,box-shadow] duration-150"
    :class="{
      'ml-[-277px] !shadow-none': isMobile && collapsed,
      'hidden': !isMobile && !authStore.isUserLogged
    }"
  >
    <a :href="dashboardURL">
      <ErankLogo class="flex-none" :collapsed="collapseState" :plan="subscriptionStore.currentPlan" />
    </a>
    <div class="frs-hide-scroll relative grow overflow-y-scroll">
      <nav class="h-full">
        <template v-for="(route, index) in routesList" :key="index">
          <template v-if="'type' in route">
            <div class="bg-[--v3-theme-primarycolor] px-[20px] py-[15px]">
              <hr
                class="divider m-0 border-t transition-colors duration-150"
                :class="collapseState
                  ? 'border-[--v3-theme-primarydarkercolor]'
                  : 'border-[--color-brand-forrest-green-80]'"
              >
            </div>
          </template>
          <template v-else-if="'items' in route">
            <NavMenuGroup
              :text="route.text"
              :icon="route.icon"
              :routes="route.items"
              :active="isRouteActive(route)"
              :collapsed="collapseState"
              :expanded="openMenu === route.id"
              @toggle-menu="handleToggleMenu(route.id)"
            >
              <NavMenuGroupItem
                v-for="(routeItem , routeIndex) in route.items"
                :key="`route-${routeIndex}`"
                :to="routeItem.to"
                :legacy-url="routeItem.legacy"
                :name="routeItem.text"
                :active="isRouteActive(routeItem)"
                :collapsed="collapseState"
              />
            </NavMenuGroup>
          </template>
          <template v-else>
            <NavMenuItem
              :text="route.text"
              :icon="route.icon"
              :legacy-url="route.legacy"
              :to="route.to"
              :active="isRouteActive(route)"
              :collapsed="collapseState"
              :target="route.target"
            />
          </template>
        </template>
      </nav>
    </div>
    <div
      class="flex flex-none flex-col transition-[gap] duration-150"
      :class="{
        'items-center gap-[12px]': collapseState,
      }"
    >
      <UpgradeButton :collapsed="collapseState" :plan="subscriptionStore.currentPlan" />
      <div
        class="flex transition-[gap] duration-150"
        :class="{
          'flex-col items-center gap-[12px]': collapseState,
          '-mt-px border border-x-0 border-[--v3-theme-primarycolor] pl-[225px]': !authStore.isUserLogged,
        }"
      >
        <UserProfile
          v-if="authStore.isUserLogged"
          :avatar="authStore.user.current_shop?.image_url_75x75"
          :collapsed="collapseState"
          :is-loading="isPendingShops"
          :name="shop?.name"
        >
          <UserSettings />
        </UserProfile>
        <CollapseButton
          :class="{
            '-mt-px': !authStore.isUserLogged
          }"
          :collapsed="collapseState"
          @toggle-collapse="handleToggleCollapse($event)"
        />
      </div>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useMobile } from '@/common/composables/application';
import { useAuthStore } from '@/common/stores/auth';
import { useSubscriptionStore } from '@/common/stores/subscription';
import { storage } from '@/helpers/local-storage';
import CollapseButton from '@/modules/layout/components/atoms/CollapseButton';
import ErankLogo from '@/modules/layout/components/atoms/ErankLogo';
import NavMenuGroupItem from '@/modules/layout/components/atoms/NavMenuGroupItem';
import NavMenuItem from '@/modules/layout/components/atoms/NavMenuItem';
import UpgradeButton from '@/modules/layout/components/atoms/UpgradeButton';
import NavMenuGroup from '@/modules/layout/components/molecules/NavMenuGroup';
import UserProfile from '@/modules/layout/components/molecules/UserProfile';
import UserSettings from '@/modules/layout/components/organisms/UserSettings';
import { useSidebarStore } from '@/modules/layout/stores/sidebar';
import { useShops } from '@/queries/shop';

import type { ActiveRouteConfig } from './menu-routes';
import { isSidebarRouteActive, publicRoutes, registeredRoutes } from './menu-routes';

const { isMobile } = useMobile();

const openMenu = shallowRef<string | null>(null);
const sidebarStore = useSidebarStore();
const { toggleSidebar } = sidebarStore;
const { collapsed, sideBarCollapsePreference } = storeToRefs(sidebarStore);
const route = useRoute();
const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const dashboardURL = '/dashboard';
const { data: shops, isPending: isPendingShops } = useShops();

const collapseState = computed<boolean>(() => isMobile.value ? false : collapsed.value);
const currentRouteNames = computed<string[]>(() => {
  const matchedRouteNames = route.matched
    .map(routeRecord => routeRecord.name)
    .filter((routeName): routeName is string => typeof routeName === 'string');

  if (typeof route.name === 'string' && !matchedRouteNames.includes(route.name)) {
    matchedRouteNames.push(route.name);
  }

  return matchedRouteNames;
});
const routesList = computed(() => authStore.isUserLogged ? registeredRoutes : publicRoutes);
const shop = computed(() => shops.value?.find(shop => shop.id === authStore.user.current_shop_id));

const isRouteActive = (menuRoute: ActiveRouteConfig): boolean => {
  return isSidebarRouteActive(menuRoute, currentRouteNames.value, route.query);
};

const activeGroupId = computed<string | null>(() => {
  const activeGroup = routesList.value.find(menuRoute => 'items' in menuRoute && isRouteActive(menuRoute));

  return activeGroup && 'id' in activeGroup && typeof activeGroup.id === 'string' ? activeGroup.id : null;
});

const handleToggleMenu = (routeId: string) => {
  openMenu.value = (collapsed.value || openMenu.value !== routeId) ? routeId : null;
  if (collapsed.value) {
    collapsed.value = false;
    toggleSidebar();
  }
};

const handleToggleCollapse = (value: boolean) => {
  collapsed.value = value;
  toggleSidebar();
};

onMounted(() => {
  if (!isMobile.value) {
    document.body.style.setProperty('--sidebar-width', collapsed.value ? '50px' : '277px');
  }
});

watch(() => route.fullPath, () => {
  if (activeGroupId.value) {
    openMenu.value = activeGroupId.value;
  }

  if(isMobile.value) {
    handleToggleCollapse(true);
  }
}, { immediate: true });

watch(() => sideBarCollapsePreference.value, (newValue) => {
  storage.set<boolean>('sidebar-collapsed', newValue === undefined ? false : newValue);
}, { immediate: true });
</script>
